<script setup lang="ts">
// icons
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

// components
import CiReviewSlide from '@/components/CiReview/CiReviewSlide.vue';
import CiReviewSlideMore from '@/components/CiReview/CiReviewSlideMore.vue';
import CiSkeletonReviewSlider from '@/components/skeleton/CiSkeletonReviewSlider.vue';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiReviewSliderProps {
  campsiteSlug: string;
  initialHasReviews?: boolean;
}
const props = withDefaults(defineProps<CiReviewSliderProps>(), {
  initialHasReviews: true,
});

/********************
 * COMPOSITIONS      *
 ********************/
// const route = useRoute();
const localePath = useLocalePath();
const reviewService = useReviewService();
const { $gettext } = useGettext();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const hasReviews = ref(props.initialHasReviews);
const limit = ref(9);

const user = computed(() => {
  const additionalReviewCount = data.value?.results?.length - reviewCount.value;
  const users = [];

  for (let i = 0; i < additionalReviewCount; i += 1) {
    const image = data.value?.results?.[reviewCount.value + i].user_profile_picture;
    users.push(image);
  }
  return users;
});

const reviewCount = computed(() => {
  if (data.value?.results?.length < 6) {
    return data.value?.results?.length;
  }
  return 6;
});

/********************
 * INITIALIZATION    *
 ********************/
const { data, pending } = await useLazyAsyncData(`reviews-${props.campsiteSlug}`, getReviews);

async function getReviews() {
  try {
    const response = await reviewService.getReviews(locale.value, props.campsiteSlug, {
      limit: limit.value,
      offset: 0,
    });
    // this.$emit('review-slider-count', reviews.count);
    hasReviews.value = !!response.results.length;
    return response;
  } catch (error) {
    return error;
  }
}
</script>

<template>
  <div
    v-if="hasReviews && data"
    class="mt-12 pt-12"
  >
    <h3
      v-if="!pending"
      class="text-center"
      v-html="$gettext('<strong>What guests</strong> write', true)"
    />

    <div
      v-if="!pending"
      class="mt-4"
    >
      <div class="review-slider__container">
        <CiCssSlider v-if="data.results?.length">
          <div
            v-for="index in reviewCount"
            :key="data.results?.[index - 1].id"
            class="slide"
            :data-idx="index"
          >
            <CiReviewSlide
              :review="data.results?.[index - 1]"
              :campsite-id="props.campsiteSlug"
            />
          </div>
          <div
            v-if="data.results.length > reviewCount"
            class="slide"
            :data-idx="reviewCount + 1"
          >
            <CiReviewSlideMore
              :campsite-id="props.campsiteSlug"
              :count="data.results.length - reviewCount"
              :users="user"
            />
          </div>
        </CiCssSlider>
      </div>
    </div>
    <div
      v-else
      class="container"
    >
      <CiSkeletonReviewSlider />
    </div>

    <div class="text-center">
      <CiButton
        v-if="hasReviews"
        tag="NuxtLink"
        variant="secondary"
        :href="localePath({ name: 'campsite-identifier-reviews', params: { identifier: props.campsiteSlug } })"
        class="mt-4"
      >
        {{ $gettext('Read all reviews') }}
        <CiAwesomeIcon
          class="ml-2"
          :icon="faChevronRight"
          ratio="0.6"
        />
      </CiButton>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.slide {
  @apply relative snap-center lg:snap-start;
  flex: 1 0 300px;
  max-width: 300px;
}

.slide:first-of-type {
  @apply ml-[15px];
}

.slide:last-of-type {
  @apply mr-[15px];
}

@screen sm {
  .review-slider__container {
    @apply p-0;
  }

  .slide:first-of-type {
    margin-left: calc((100vw - 540px) / 2 + 15px);
  }

  .slide:last-of-type {
    margin-right: calc((100vw - 540px) / 2 + 15px);
  }
}

@screen md {
  .review-slider__container {
    padding: 0;
  }

  .slide {
    flex: 1 0 320px;
    max-width: 320px;
  }

  .slide:first-of-type {
    margin-left: calc((100vw - 720px) / 2 + 15px);
  }

  .slide:last-of-type {
    margin-right: calc((100vw - 720px) / 2 + 15px);
  }
}

@screen lg {
  .review-slider__container {
    max-width: 930px;
    margin: auto;
  }

  .slide {
    flex: 1 0 300px;
    max-width: 300px;
  }

  .slide:first-of-type {
    margin-left: auto;
  }

  .slide:last-of-type {
    margin-right: auto;
  }
}

@screen xl {
  .review-slider__container {
    max-width: 1110px;
  }

  .slide {
    flex: 1 0 360px;
    max-width: 360px;
  }
}
</style>
